import { useCallback, useEffect, useRef, useState } from 'react';
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
  type LexicalEditor,
} from 'lexical';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HeadingNode, QuoteNode, $isHeadingNode, $createHeadingNode } from '@lexical/rich-text';
import { ListNode, ListItemNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list';
import { LinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { $setBlocksType } from '@lexical/selection';
import { mergeRegister } from '@lexical/utils';
import { ImageNode, $createImageNode } from '@/components/base/ImageNode';

type UploadState = 'idle' | 'uploading';

interface RichTextEditorProps {
  initialHtml?: string;
  onChange: (html: string) => void;
  onUploadImage: (file: File) => Promise<string | null>;
}

const editorTheme = {
  paragraph: 'text-sm text-brand-deep leading-relaxed mb-2',
  heading: {
    h1: 'text-xl font-bold text-brand-deep mt-6 mb-3',
    h2: 'text-lg font-bold text-brand-deep mt-5 mb-2',
    h3: 'text-base font-semibold text-brand-deep mt-4 mb-2',
  },
  list: {
    ul: 'text-sm text-brand-deep list-disc ml-5 mb-2',
    ol: 'text-sm text-brand-deep list-decimal ml-5 mb-2',
    listitem: 'mb-1',
  },
  link: 'text-brand-brown underline cursor-pointer',
  text: {
    bold: 'font-bold',
    italic: 'italic',
    underline: 'underline',
    strikethrough: 'line-through',
  },
  image: 'max-w-full h-auto rounded-lg my-3',
  quote: 'border-l-2 border-brand-cream pl-4 italic text-gray-500 my-3',
};

function InitializeContentPlugin({ html }: { html?: string }) {
  const [editor] = useLexicalComposerContext();
  const didInit = useRef(false);

  useEffect(() => {
    if (didInit.current || !html) return;
    didInit.current = true;
    editor.update(() => {
      const parser = new DOMParser();
      const dom = parser.parseFromString(html, 'text/html');
      const nodes = $generateNodesFromDOM(editor, dom);
      const root = $getRoot();
      root.clear();
      root.append(...nodes);
    });
  }, [html, editor]);

  return null;
}

function OnChangePlugin({ onChange }: { onChange: (html: string) => void }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const unregister = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor);
        onChange(html);
      });
    });
    return unregister;
  }, [editor, onChange]);

  return null;
}

// 拦截粘贴图片，上传后插入 URL 而非 base64
function PasteImagePlugin({ onUploadImage }: { onUploadImage: (file: File) => Promise<string | null> }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const root = editor.getRootElement();
    if (!root) return;

    const handlePaste = async (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (!item.type.startsWith('image/')) continue;

        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;

        const url = await onUploadImage(file);
        if (!url) continue;

        editor.update(() => {
          const imageNode = $createImageNode({ src: url, altText: '', maxWidth: 800 });
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            selection.insertNodes([imageNode]);
          }
        });
      }
    };

    root.addEventListener('paste', handlePaste);
    return () => root.removeEventListener('paste', handlePaste);
  }, [editor, onUploadImage]);

  return null;
}

function ToolbarPlugin({ onUploadImage }: { onUploadImage: (file: File) => Promise<string | null> }) {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [blockType, setBlockType] = useState('paragraph');
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const updateBlockType = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      const anchorNode = selection.anchor.getNode();
      const element = anchorNode.getTopLevelElement();
      if (element && $isHeadingNode(element)) {
        setBlockType(element.getTag());
      } else {
        setBlockType('paragraph');
      }
    });
  }, [editor]);

  const updateFormats = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
    });
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(() => {
        updateBlockType();
        updateFormats();
      }),
      editor.registerRootListener(() => {
        updateBlockType();
        updateFormats();
      }),
    );
  }, [editor, updateBlockType, updateFormats]);

  const formatHeading = (tag: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      if (tag === 'paragraph') {
        $setBlocksType(selection, () => $createParagraphNode());
      } else {
        $setBlocksType(selection, () => $createHeadingNode(tag as 'h1' | 'h2' | 'h3'));
      }
    });
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadState('uploading');
    const url = await onUploadImage(file);
    setUploadState('idle');
    if (!url) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    editor.update(() => {
      const imageNode = $createImageNode({ src: url, altText: '', maxWidth: 800 });
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        selection.insertNodes([imageNode]);
      }
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleLinkClick = () => {
    const url = window.prompt('输入链接地址（留空则取消链接）：');
    if (url !== null) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, url || null);
    }
  };

  const btnClass = (active: boolean, disabled = false) =>
    `w-7 h-7 flex items-center justify-center rounded-md text-xs transition-colors ${
      disabled
        ? 'text-gray-200 cursor-not-allowed'
        : active
          ? 'bg-[var(--color-brand-deep,#4a3728)] text-white cursor-pointer'
          : 'text-gray-400 hover:text-[var(--color-brand-deep,#4a3728)] hover:bg-[var(--color-brand-light,#f5f0eb)] cursor-pointer'
    }`;

  return (
    <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-brand-light/30 flex-wrap bg-brand-gray/50">
      <button
        onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
        className={btnClass(false)}
        title="撤销"
        type="button"
      >
        <i className="ri-arrow-go-back-line" />
      </button>
      <button
        onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
        className={btnClass(false)}
        title="重做"
        type="button"
      >
        <i className="ri-arrow-go-forward-line" />
      </button>
      <span className="w-px h-5 bg-brand-light/50 mx-1" />

      <button
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
        className={btnClass(isBold)}
        title="加粗"
        type="button"
      >
        <i className="ri-bold" />
      </button>
      <button
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
        className={btnClass(isItalic)}
        title="斜体"
        type="button"
      >
        <i className="ri-italic" />
      </button>
      <button
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
        className={btnClass(isUnderline)}
        title="下划线"
        type="button"
      >
        <i className="ri-underline" />
      </button>
      <button
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
        className={btnClass(isStrikethrough)}
        title="删除线"
        type="button"
      >
        <i className="ri-strikethrough" />
      </button>
      <span className="w-px h-5 bg-brand-light/50 mx-1" />

      <select
        value={blockType}
        onChange={(e) => formatHeading(e.target.value)}
        className="text-[11px] px-2 py-1 rounded-md border border-brand-light bg-white text-brand-deep cursor-pointer outline-none h-7"
      >
        <option value="paragraph">正文</option>
        <option value="h1">标题1</option>
        <option value="h2">标题2</option>
        <option value="h3">标题3</option>
      </select>
      <span className="w-px h-5 bg-brand-light/50 mx-1" />

      <button
        onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)}
        className={btnClass(false)}
        title="无序列表"
        type="button"
      >
        <i className="ri-list-unordered" />
      </button>
      <button
        onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)}
        className={btnClass(false)}
        title="有序列表"
        type="button"
      >
        <i className="ri-list-ordered" />
      </button>
      <span className="w-px h-5 bg-brand-light/50 mx-1" />

      <button
        onClick={handleLinkClick}
        className={btnClass(false)}
        title="插入链接"
        type="button"
      >
        <i className="ri-link" />
      </button>
      <button
        onClick={handleImageClick}
        className={btnClass(isBold, uploadState === 'uploading')}
        disabled={uploadState === 'uploading'}
        title="插入图片"
        type="button"
      >
        {uploadState === 'uploading' ? (
          <i className="ri-loader-4-line animate-spin" />
        ) : (
          <i className="ri-image-add-line" />
        )}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileChange}
        className="hidden"
      />

      {uploadState === 'uploading' && (
        <span className="text-[10px] text-gray-400 ml-1">正在上传图片...</span>
      )}
    </div>
  );
}

export default function RichTextEditor({ initialHtml, onChange, onUploadImage }: RichTextEditorProps) {
  const initialConfig = {
    namespace: 'NewsContentEditor',
    theme: editorTheme,
    nodes: [HeadingNode, ListNode, ListItemNode, LinkNode, QuoteNode, ImageNode],
    onError: (error: Error) => {
      console.error('RichTextEditor error:', error);
    },
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className="border border-brand-light rounded-xl overflow-hidden bg-white">
        <ToolbarPlugin onUploadImage={onUploadImage} />
        <RichTextPlugin
          contentEditable={
            <ContentEditable className="min-h-[320px] px-4 py-3 outline-none text-sm text-brand-deep" />
          }
          placeholder={
            <div className="absolute top-3 left-4 text-gray-300 text-sm pointer-events-none select-none">
              开始输入正文内容... 可插入图片、调整格式、添加链接等
            </div>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
      </div>
      <InitializeContentPlugin html={initialHtml} />
      <OnChangePlugin onChange={onChange} />
      <PasteImagePlugin onUploadImage={onUploadImage} />
      <HistoryPlugin />
    </LexicalComposer>
  );
}